import axios from 'axios';
import { useState } from 'react';
import ResultBox from './components/ResultBox';
import TextfieldBox from './components/TextfieldBox';
import TitleBox from './components/TitleBox';
import {
  Wrapper,
  Container,
  Box,
  Title,
  IndicatorList,
  IndicatorItem,
  FormBox,
  ButtonBox,
  Button,
  AttachmentBox,
  FlexBox,
} from './App.styles';

const API_BASE_URL = '/api';

function App() {
  const [step, setStep] = useState(1); // 1: 업로드, 2: 입력, 3: 완료
  const [file, setFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processedFileId, setProcessedFileId] = useState(null);

  // 파일 선택 핸들러
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  // 파일 업로드 및 분석
  const handleUpload = async () => {
    if (!file) {
      setError('파일을 선택해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'json',
        responseEncoding: 'utf8',
      });

      setFileInfo(response.data);

      // 입력 필드 초기화
      const initialInputs = {};
      response.data.matches.forEach((match) => {
        initialInputs[match.placeholder] = '';
      });
      setInputs(initialInputs);

      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || '파일 업로드 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 텍스트 교체 및 처리
  const handleReplace = async () => {
    // 모든 필드가 채워졌는지 확인
    const missingFields = fileInfo.matches.filter(
      (match) => !inputs[match.placeholder] || inputs[match.placeholder].trim() === '',
    );

    if (missingFields.length > 0) {
      setError('모든 필드를 채워주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const replacements = fileInfo.matches.map((match) => ({
        placeholder: match.placeholder,
        value: inputs[match.placeholder],
      }));

      const response = await axios.post(
        `${API_BASE_URL}/replace`,
        {
          fileId: fileInfo.fileId,
          replacements: replacements,
          originalContent: fileInfo.originalContent,
          originalFilename: fileInfo.filename,
          fileType: fileInfo.fileType,
        },
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
          responseEncoding: 'utf8',
        },
      );

      setProcessedFileId(response.data.fileId);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || '파일 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getSafeFilename = (id, urlArray) => {
    // 1. 유효한 ID가 있는지 확인 (공백 제거 후)
    const cleanId = id?.trim();
    if (cleanId) return cleanId;

    // 2. URL 배열의 마지막 요소가 있는지 확인
    const lastFromUrl = urlArray?.at(-1);
    if (lastFromUrl && lastFromUrl !== '') return lastFromUrl;

    // 3. 둘 다 없으면 임의값 생성 (UUID 또는 타임스탬프)
    const randomId =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID().split('-')[0] // 너무 길면 앞부분만 사용
        : Date.now().toString(36);

    return `document_${randomId}.docx`;
  };

  // 파일 다운로드
  const handleDownload = async () => {
    const response = await axios({
      url: `/api/download/${processedFileId}`,
      method: 'GET',
      responseType: 'blob',
    });

    // 브라우저 메모리에 가상 URL 생성 후 다운로드
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', getSafeFilename(processedFileId, response.config.url));
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // 처음부터 다시 시작
  const handleReset = () => {
    setStep(1);
    setFile(null);
    setFileInfo(null);
    setInputs({});
    setProcessedFileId(null);
    setError(null);
  };

  return (
    <>
      <Wrapper>
        <Container>
          <Box>
            <Title>문서 자동 채우기</Title>

            {step === 1 || step === 2 ? (
              <IndicatorList>
                <IndicatorItem $isActive={step === 1}>1</IndicatorItem>
                <IndicatorItem $isActive={step === 2}>2</IndicatorItem>
              </IndicatorList>
            ) : null}

            {error && <div className="error-message">{error}</div>}

            {step === 1 && (
              <>
                <TitleBox title="파일 업로드"></TitleBox>
                <FormBox>
                  <AttachmentBox>
                    <FlexBox $gap="10px" $margin="0 0 8px">
                      <span className="attachment_btn">
                        <label htmlFor="attachment">파일첨부</label>
                        <input type="file" id="attachment" onChange={handleFileChange} accept=".txt,.docx" />
                      </span>
                      <span className="attachment_name">
                        <span className="name_box">
                          {file && (
                            <>
                              <span className="name_text">{file.name}</span>
                              <button
                                className="remove"
                                onClick={() => {
                                  setFile(null);
                                }}
                              >
                                <img src="/img/icon_remove.svg" alt="삭제" />
                              </button>
                            </>
                          )}
                        </span>
                      </span>
                    </FlexBox>
                    <span className="guide_desc">업로드 가능 파일 : .txt, .docx</span>
                  </AttachmentBox>
                </FormBox>
                <ButtonBox>
                  <Button onClick={handleUpload} disabled={!file || loading}>
                    {loading ? '처리 중...' : '업로드 및 분석'}
                  </Button>
                </ButtonBox>
              </>
            )}
            {step === 2 && (
              <>
                <TitleBox title="텍스트 입력"></TitleBox>
                <FormBox>
                  {Object.keys(inputs).map((key) => {
                    return (
                      <TextfieldBox
                        label={key}
                        key={key}
                        id={key}
                        value={inputs[key]}
                        onChange={(e) => {
                          setInputs({ ...inputs, [key]: e.target.value });
                        }}
                      />
                    );
                  })}
                </FormBox>
                <ButtonBox>
                  <Button
                    onClick={() => {
                      setStep(1);
                      setError(null);
                      setFileInfo({});
                      setInputs(null);
                    }}
                    $isNotSingle
                    $secondary
                  >
                    뒤로
                  </Button>
                  <Button onClick={handleReplace} disabled={loading} $isNotSingle>
                    {loading ? '처리 중...' : '문서 생성'}
                  </Button>
                </ButtonBox>
              </>
            )}

            {step === 3 && (
              <>
                <ResultBox />
                <ButtonBox>
                  <Button onClick={handleReset} $isNotSingle $secondary>
                    새 문서 처리
                  </Button>
                  <Button onClick={handleDownload} $isNotSingle>
                    파일 다운로드
                  </Button>
                </ButtonBox>
              </>
            )}
          </Box>
        </Container>
      </Wrapper>
    </>
  );
}

export default App;
